//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ButtonAction from '@react/components/elements/ButtonAction'
import { chainActions } from '@react/redux/chainSlice'
import React, { ReactElement } from 'react'

import PowerIcon from '@react/components/icons/PowerIcon'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { ERC20Wei, erc20WeiToCommaDecString } from '@shared/conversions'
import { RoleEnum } from '@shared/Enums'
import { ethers, ZeroAddress } from 'ethers'
import { useNavigate } from 'react-router-dom'

import { useAppKitAccount, useDisconnect } from '@reown/appkit/react'
import { logOT } from '@shared/loggingShared'
import { useAuth } from 'react-oidc-context'
import { Erc20Abi } from '@shared/Abis'
import { ChainAccountValues, LiquidityAsset } from '@shared/LiquidityAsset'
import { LOCAL_COGNITO_USER } from '@react/chainThunks/loginToChainThunk'

export const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  borderRadius: '20px',
  border: 0,
  boxShadow: '0',
  p: 4,
}

function LogoutButton(): React.ReactElement {
  const dispatch = useOtDispatch()
  const { disconnect } = useDisconnect()
  const {
    stageConfiguration: { domain, cognitoConfig },
  } = useOtChainState()
  const { clientId: cognitoClientId, userPoolId: cognitoUserPoolId } = cognitoConfig ?? {}

  const auth = useAuth()

  async function logoutFromCognito() {
    await dispatch(chainActions.odicDisconnect())

    const logoutPoolIdAdjusted = cognitoUserPoolId?.replace('_', '').toLowerCase()
    const logoutUriAdjusted = `https://${domain.replace('app', 'admin')}`
    const cognitoDomain = `https://${logoutPoolIdAdjusted}.auth.us-east-1.amazoncognito.com`
    const ref = `${cognitoDomain}/logout?client_id=${cognitoClientId}&logout_uri=${encodeURIComponent(
      logoutUriAdjusted
    )}`
    window.location.href = ref
  }
  function logoutAction() {
    logOT.info(' logoutAction start')
    disconnect()
      .then(() => {
        logOT.info('Disconnected from AppKit')
        dispatch(chainActions.logoffUser())
        logOT.info('OdicLoginWrapper calling auth.signoutRedirect')
        if (auth.isAuthenticated) {
          return logoutFromCognito()
        }
      })

      .catch((error) => {
        logOT.error(' LogoutButton error', error)
      })
  }

  return (
    <ButtonAction
      sx={{ marginBottom: 3, backgroundColor: '#F2F2F2' }}
      startIcon={<PowerIcon />}
      variant={'tertiary'}
      onClick={() => {
        logoutAction()
      }}
    >
      Disconnect
    </ButtonAction>
  )
}

export default function AccountButtonMenu(): ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const {
    userConnection: { accountBalances, accountAddr, roleEnum },
    chainConfiguration,

    cognitoUser,
    chainConfiguration: { chainName, chainId, perimeterAddresses, providerReadOnly },
  } = useOtChainState()

  const navigate = useNavigate()
  const dispatch = useOtDispatch()
  const { disconnect } = useDisconnect()
  const { embeddedWalletInfo } = useAppKitAccount({ namespace: 'eip155' })

  async function fetchAccountBalances() {
    if (perimeterAddresses) {
      const accountBalances: ChainAccountValues = {}
      for (const liquidityAsset of perimeterAddresses.liquidityAssets) {
        const tokenContract = new ethers.Contract(liquidityAsset.addr, Erc20Abi, providerReadOnly)
        const balance = await tokenContract.balanceOf(accountAddr)

        accountBalances[liquidityAsset.addr] = balance.toString()
      }
      dispatch(chainActions.updateAccountBalances(accountBalances))
    }
  }
  const handleClose = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
    setAnchorEl(null)
  }
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
    fetchAccountBalances()
  }
  const handleDemoCLick = (): void => {
    navigate('/demo')
    handleClose()
  }
  const handleCompanyProfilesClick = (): void => {
    navigate('/companyProfiles')
    handleClose()
  }

  const handleReportsCLick = (): void => {
    navigate('/reports')
    handleClose()
  }
  const handleLiquidityAssetsClick = (): void => {
    navigate('/liquidityAssets')
    handleClose()
  }

  const handleDisconnect = (): void => {
    logOT.info('Disconnecting from AppKit')
    disconnect()
    dispatch(chainActions.logoffUser())
  }

  if (!chainConfiguration || !accountAddr || accountAddr === ZeroAddress) {
    return <> </>
  }

  return (
    <>
      <ButtonAction variant={'tertiary'} onClick={handleMenuOpen}>
        {(accountAddr ? accountAddr : ZeroAddress).substring(0, 6) + '...'}
      </ButtonAction>
      <Drawer
        anchor="right"
        open={openMenu}
        onClose={handleClose}
        sx={{
          zIndex: 1300,
          '& .MuiDrawer-paper': {
            width: 400,
            bgcolor: 'white',
            padding: 4,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header with OpenTrade title and close button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '24px' }}>
              OpenTrade
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                padding: 1,
                border: '2px solid #ccc',
                borderRadius: '8px',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Logout/Disconnect Button */}
          {cognitoUser?.access_token !== LOCAL_COGNITO_USER.access_token ? (
            <LogoutButton />
          ) : (
            <ButtonAction
              sx={{ marginBottom: 3, backgroundColor: '#F2F2F2' }}
              startIcon={<PowerIcon />}
              variant={'tertiary'}
              onClick={handleDisconnect}
            >
              Disconnect
            </ButtonAction>
          )}

          {/* Email Section */}
          {(embeddedWalletInfo?.user?.email || cognitoUser?.profile?.email) && (
            <Box sx={{ marginBottom: 3 }}>
              <Typography sx={{ color: '#999', fontSize: '14px', marginBottom: 0.5 }}>
                {embeddedWalletInfo?.user?.email ?? cognitoUser?.profile?.email ?? ''}
              </Typography>
            </Box>
          )}

          {/* Chain ID Section */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography sx={{ color: '#999', fontSize: '14px', marginBottom: 0.5 }}>Chain ID</Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {chainName} {chainId}
            </Typography>
          </Box>

          {/* Separator before Liquidity Asset Balances */}
          <Divider sx={{ marginY: 2, backgroundColor: '#e0e0e0' }} />

          {/* Liquidity Asset Balances Section */}
          <Box sx={{ marginVertical: 4, paddingVertical: 1 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 2 }}>
              Liquidity Asset Balances
            </Typography>
            {Object.entries(accountBalances ?? {}).map(([addr, balance]) => {
              const liquidityAsset = perimeterAddresses?.liquidityAssets.find((lq: LiquidityAsset) => lq.addr == addr)
              return (
                <Box sx={{ marginBottom: 2 }} key={addr}>
                  <Typography sx={{ color: '#999', fontSize: '14px', marginBottom: 0.5 }}>
                    {liquidityAsset?.name ?? 'Unknown Asset'}
                  </Typography>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                    {erc20WeiToCommaDecString(balance as ERC20Wei)}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          {/* Separator after Liquidity Asset Balances */}
          <Divider sx={{ marginY: 2, backgroundColor: '#e0e0e0' }} />

          {/* Admin tasks Section */}
          {(roleEnum === RoleEnum.BORROWER ||
            roleEnum === RoleEnum.POOL_ADMIN ||
            roleEnum === RoleEnum.PROTOCOL_MANAGER ||
            roleEnum === RoleEnum.LENDER) && (
            <Box sx={{ marginVertical: 4, paddingVertical: 1 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 1 }}>
                {roleEnum === RoleEnum.LENDER ? 'Settings' : 'Admin tasks'}
              </Typography>

              {/* Reports */}
              {(roleEnum === RoleEnum.BORROWER ||
                roleEnum === RoleEnum.POOL_ADMIN ||
                roleEnum === RoleEnum.PROTOCOL_MANAGER) && (
                <Typography
                  sx={{
                    fontSize: '16px',
                    marginBottom: 1,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={handleReportsCLick}
                >
                  Reports
                </Typography>
              )}

              {/* Company Profiles */}
              <Typography
                sx={{
                  fontSize: '16px',
                  marginBottom: 1,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={
                  roleEnum === RoleEnum.LENDER
                    ? () => {
                        navigate('/companyProfile')
                        handleClose()
                      }
                    : handleCompanyProfilesClick
                }
              >
                Company Profile{roleEnum === RoleEnum.LENDER ? '' : 's'}
              </Typography>

              {/* Demo Management */}
              {(roleEnum === RoleEnum.BORROWER ||
                roleEnum === RoleEnum.POOL_ADMIN ||
                roleEnum === RoleEnum.PROTOCOL_MANAGER) && (
                <Typography
                  sx={{
                    fontSize: '16px',
                    marginBottom: 1,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={handleDemoCLick}
                >
                  Demo Management
                </Typography>
              )}

              {/* Liquidity Assets - only for PROTOCOL_MANAGER */}
              {roleEnum === RoleEnum.PROTOCOL_MANAGER && (
                <Typography
                  sx={{
                    fontSize: '16px',
                    marginBottom: 1,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={handleLiquidityAssetsClick}
                >
                  Liquidity Assets
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  )
}
